<script lang="ts">
  import {
    StickyNote,
    ChevronDown,
    ChevronRight,
    ChevronLeft
  } from "lucide-svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import { fade, slide } from "svelte/transition"
  import { Stretch } from "svelte-loading-spinners"
  import DepartmentGoals from "./Index/DepartmentGoals.svelte"
  import Notes from "./Index/Notes.svelte"
  import BookReading from "./Index/BookReading.svelte"
  import Birthdays from "./Index/Birthdays.svelte"
  import MemberBadges from "./Index/MemberBadges.svelte"
  import WorkAnniversary from "./Index/WorkAnniversary.svelte"
  import {
    xhr,
    formatDate,
    resumeOnboarding,
    cancelOnboarding
  } from "$app/utils"
  import Recognition from "$app/Pages/Plans/Recognitions/Recognition.svelte"
  import Onboarding from "$app/Pages/Plans/OnePageView/Onboarding.svelte"
  import { buildUserList } from "$app/utils/user_methods"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    department_goals: any[]
    tasks: TaskType[]
    date_start: string
    date_end: string
    current_user: UserType
    onboarding: any
    users: { [key: number]: UserType }
    is_leadership: boolean
    update_goals: boolean
  }

  let props = $props()
  let {
    plan,
    tasks = [],
    date_start,
    date_end,
    current_user,
    onboarding,
    users = {},
    is_leadership = false,
    update_goals = false
  }: Props = props

  let recognitions: RecognitionType[] = $state([])
  let department_goals: any[] = $state(props.department_goals || [])

  appStore.date_start_str = date_start
  appStore.date_end_str = date_end
  appStore.users = appStore.convertUsersHash(users)
  appStore.is_leadership = is_leadership
  appStore.update_goals = update_goals

  let dateStart = $state(new Date(`${date_start}T00:00:00`))
  let dateEnd = $state(new Date(`${date_end}T23:59:59`))

  let activitiesPromise = $state(Promise.resolve())
  activitiesPromise = fetchData(date_start)
  historyState(date_start)

  let visibility = $state({
    notes: false,
    recognitions: true,
    goals: true
  })

  let bookReading = $state(null)
  let birthdays = $state([])
  let workAnniversaries = $state([])
  let memberBadges = $state([])
  let weeklyQuarters = $state({
    start_quarter: 0,
    end_quarter: 0,
    transition_week: false
  })

  /**
   * Moves the calendar in weeks and fetches for activities
   * @param days Integer
   */
  async function moveWeek(days) {
    const tmpDateStart = new Date(
      dateStart.getTime() + days * 24 * 60 * 60 * 1000
    )
    const dateStartStr = tmpDateStart.toJSON().split("T")[0]

    activitiesPromise = fetchData(dateStartStr)
  }

  async function fetchData(date: string) {
    try {
      recognitions = []
      tasks = []
      department_goals = []
      const resp = await xhr.get(
        `/plans/${plan.id}/activities.json?date=${date}`
      )
      department_goals = resp.data.department_goals
      tasks = resp.data.tasks
      recognitions = resp.data.recognitions
      date_start = resp.data.date_start
      date_end = resp.data.date_end
      dateStart = new Date(`${date_start}T00:00:00`)
      dateEnd = new Date(`${date_end}T23:59:59`)
      appStore.date_start_str = date_start
      appStore.date_end_str = date_end
      appStore.dateStart = dateStart
      appStore.dateEnd = dateEnd

      bookReading = resp.data.book_reading
      birthdays = resp.data.birthdays
      workAnniversaries = resp.data.work_anniversaries
      weeklyQuarters = resp.data.weekly_quarters
      memberBadges = resp.data.member_badges
    } catch (e) {
      console.log(e)
      alert("There was an error fetching the data. Please try again.")
    }

    historyState(date)
  }

  let currentGoal = null
  let openModal = false
  function openModalHandler(event) {
    currentGoal = event.detail
    openModal = true
  }

  function historyState(dateSt: string): void {
    window.history.pushState({ date: dateSt }, "Activities", `?date=${dateSt}`)
  }
</script>

<Layout {...props}>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-8 py-6">
    {#if onboarding.completed_onboarding}
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div class="flex my-4 w-full justify-center">
          <button
            class="w-10 h-10 bg-lwp-light-blue text-blue-600 rounded-full flex items-center justify-center"
            onclick={() => moveWeek(-7)}
          >
            <ChevronLeft class="size-8" />
          </button>
          <div class="bg-lwp-light-blue rounded-full py-2 px-6 mx-4">
            {formatDate(dateStart)} - {formatDate(dateEnd)}
          </div>
          <button
            class="w-10 h-10 bg-lwp-light-blue text-blue-600 rounded-full flex items-center justify-center"
            onclick={() => moveWeek(7)}
          >
            <ChevronRight class="size-8" />
          </button>
        </div>

        {#await activitiesPromise}
          <div class="grid mt-12" style="place-items:center">
            <Stretch size="80" color="gray" />
          </div>
        {:then}
          <div class="relative" transition:fade>
            {#if is_leadership}
              <div
                class="sticky top-10 right-0 z-10 rounded-xl shadow-2xl ring-1 ml-auto ring-black ring-opacity-5 {visibility.notes
                  ? 'bg-gray-100 w-11/12'
                  : 'bg-indigo-600 w-48'}"
              >
                <div class="grid grid-cols-1">
                  <div class="w-full px-4 py-3">
                    <h2 class="text-base lg:text-lg text-white">
                      <button
                        onclick={() => (visibility.notes = !visibility.notes)}
                        class="flex justify-end items-center"
                      >
                        {#if visibility.notes}
                          <ChevronDown class="text-gray-800" />
                        {:else}
                          <ChevronRight class="text-gray-200" />
                        {/if}

                        <StickyNote
                          class="size-5 {visibility.notes
                            ? 'text-gray-800'
                            : 'text-gray-200'}"
                        />
                        <span
                          class={visibility.notes
                            ? "text-gray-800"
                            : "text-gray-200"}
                        >
                          Weekly Log
                        </span>
                      </button>
                    </h2>
                    {#if visibility.notes}
                      <button
                        onclick={() => (visibility.notes = !visibility.notes)}
                        class="absolute right-4 top-4"
                        aria-label="Close Weekly Log"
                        title="Close Weekly Log"
                      >
                        <svg
                          class="h-5 w-5"
                          x-description="Heroicon name: mini/x-mark"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                          />
                        </svg>
                      </button>
                    {/if}
                  </div>
                  {#if visibility.notes}
                    <div class="relative" transition:slide={{ duration: 100 }}>
                      <div
                        class="bg-white py-3 px-4 rounded-bl-xl rounded-br-xl"
                      >
                        <Notes />
                      </div>
                    </div>
                  {/if}
                </div>
              </div>
            {/if}

            <BookReading {bookReading} />

            <div class="w-full mt-4" id="recognitions">
              <h2 class="mt-6 mb-4">
                <button
                  onclick={() =>
                    (visibility.recognitions = !visibility.recognitions)}
                  class="flex justify-start items-center"
                >
                  {#if visibility.recognitions}
                    <ChevronDown class="size-6 text-gray-800" />
                  {:else}
                    <ChevronRight class="size-6 text-gray-200" />
                  {/if}
                  <span class="text-2xl lg:text-3xl text-black ml-2"
                    >Building Our Culture and Practicing Gratefulness
                  </span>
                </button>
              </h2>

              {#if visibility.recognitions}
                <div
                  class="ml-2 lg:ml-10"
                  transition:slide={{
                    delay: 100,
                    duration: 300
                  }}
                >
                  <Birthdays {birthdays} />

                  <WorkAnniversary {workAnniversaries} />

                  <ul class="divide-y divide-gray-200 w-full">
                    {#each recognitions as recognition, idx (recognition.id)}
                      <div class="bg-white">
                        <Recognition {recognition} readOnly={true} />
                      </div>
                    {:else}
                      <div
                        class="text-center text-gray-700 text-xl font-bold bg-zinc-50 rounded-lg py-6"
                      >
                        No recognitions were sent this week.
                      </div>
                    {/each}
                  </ul>

                  <MemberBadges {memberBadges} />
                </div>
              {/if}
            </div>

            <div class="w-full mt-12" id="goals">
              <h2 class="mt-6 mb-4">
                <button
                  onclick={() => (visibility.goals = !visibility.goals)}
                  class="flex justify-start items-center gap-2"
                >
                  {#if visibility.goals}
                    <ChevronDown class="size-6 text-gray-500" />
                  {:else}
                    <ChevronRight class="size-6 text-gray-500" />
                  {/if}
                  <span class="text-2xl lg:text-3xl text-black">
                    Goal and Task Progress
                  </span>
                </button>
              </h2>

              {#if visibility.goals}
                <div
                  class="ml-2 lg:ml-10"
                  transition:slide={{
                    delay: 100,
                    duration: 300
                  }}
                >
                  {#if weeklyQuarters.transition_week}
                    <div
                      class="rounded bg-yellow-100 py-3 px-2 mb-4 shadow-sm rounded-sm"
                    >
                      <p class="text-lg lg:text-xl text-black text-center">
                        It's your Quarterly Transition Week from <span
                          class="font-bold"
                          >Q{weeklyQuarters.start_quarter}</span
                        >
                        to
                        <span class="font-bold"
                          >Q{weeklyQuarters.end_quarter}</span
                        >
                      </p>
                      <p class="mt-3 text-center">
                        We have included goals from both quarters below, please
                        close out your goals from <span class="font-bold"
                          >Q{weeklyQuarters.start_quarter}</span
                        >
                        and review your new goals for
                        <span class="font-bold"
                          >Q{weeklyQuarters.end_quarter}</span
                        >
                      </p>
                    </div>
                  {/if}
                  {#each department_goals as department}
                    <DepartmentGoals {department} {weeklyQuarters} />
                  {/each}
                </div>
              {/if}
            </div>
          </div>
        {:catch error}
          <div class="text-red-500">
            There was an error fetching the activities.
          </div>
        {/await}
      </div>
    {:else}
      <Onboarding
        onresume={() => resumeOnboarding(plan.id)}
        oncancel={() => cancelOnboarding(plan.id)}
      />

      <div class="bg-white overflow-hidden shadow">
        <div class="px-4 py-8 sm:px-6 relative">
          <h2
            class="text-2xl text-center text-gray-800 flex justify-center items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-6 h-6 mr-1"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z"
              />
            </svg>

            Activity
          </h2>

          <p class="text-lg text-center mt-6 text-gray-700">
            The Activity page will be available once you have completed the
            setup process.
          </p>
        </div>
      </div>
    {/if}
  </div>
</Layout>
